import moment from 'moment-timezone';
import { BLACKOUT_FREQUENCY } from '../consts/BlackoutPeriod.consts';

/**
 * Expand a recurrence rule into local { start, end } moments (caller converts to API strings).
 * Logic mirrors the former server-side expandRecurrence (must stay in sync).
 *
 * @param {object} rule
 * @param {string} rule.frequency
 * @param {string} rule.effectiveFrom — YYYY-MM-DD or moment
 * @param {string} rule.effectiveTo
 * @param {string} rule.startTime — HH:mm
 * @param {string} rule.endTime — HH:mm
 * @param {string} rule.timezone — IANA
 * @param {number} [rule.startDayOfWeek]
 * @param {number} [rule.endDayOfWeek]
 * @param {number} [rule.startDayOfMonth]
 * @param {number} [rule.endDayOfMonth]
 * Daily: if end time is not after start time on the clock, end is on the following calendar day.
 *
 * @returns {Array<{ start: import('moment').Moment, end: import('moment').Moment }>}
 */
export function expandRecurrence(rule) {
  const {
    frequency,
    effectiveFrom,
    effectiveTo,
    startTime,
    endTime,
    timezone,
    startDayOfWeek,
    endDayOfWeek,
    startDayOfMonth,
    endDayOfMonth,
  } = rule;

  const from = moment.tz(effectiveFrom, timezone).startOf('day');
  const to = moment.tz(effectiveTo, timezone).endOf('day');
  const [startHour, startMinute] = startTime.split(':').map((n) => parseInt(n, 10));
  const [endHour, endMinute] = endTime.split(':').map((n) => parseInt(n, 10));

  let periods;
  switch (frequency) {
    case BLACKOUT_FREQUENCY.DAILY:
      periods = expandDaily(from, to, startHour, startMinute, endHour, endMinute);
      break;
    case BLACKOUT_FREQUENCY.WEEKLY:
      periods = expandWeekly(from, to, startDayOfWeek, endDayOfWeek, startHour, startMinute, endHour, endMinute);
      break;
    case BLACKOUT_FREQUENCY.MONTHLY:
      periods = expandMonthly(from, to, startDayOfMonth, endDayOfMonth, startHour, startMinute, endHour, endMinute);
      break;
    default:
      periods = [];
  }

  return periods
    .map((period) => clampPeriodToEffectiveRange(from, to, period))
    .filter(Boolean);
}

/**
 * Clip each occurrence to the effective date range.
 * - Start before effective-from → midnight (00:00) on the effective-from calendar day
 * - End after effective-to → 23:59 on the effective-to calendar day
 *
 * @returns {{ start: import('moment').Moment, end: import('moment').Moment } | null}
 */
function clampPeriodToEffectiveRange(from, to, period) {
  let { start, end } = period;

  if (start.isAfter(to)) {
    return null;
  }
  if (end.isBefore(from)) {
    return null;
  }

  if (start.isBefore(from)) {
    start = from.clone().startOf('day');
  }

  const effectiveToEnd = to.clone().startOf('day').hour(23).minute(59).second(0).millisecond(0);
  if (end.isAfter(to)) {
    end = effectiveToEnd;
  }

  if (!end.isAfter(start)) {
    return null;
  }
  return { start, end };
}

function expandDaily(from, to, startHour, startMinute, endHour, endMinute) {
  const pairs = [];
  const d = from.clone();
  while (d.isSameOrBefore(to, 'day')) {
    const startLocal = d.clone().hour(startHour).minute(startMinute).second(0).millisecond(0);
    const endLocal = d.clone().hour(endHour).minute(endMinute).second(0).millisecond(0);
    if (!endLocal.isAfter(startLocal)) {
      endLocal.add(1, 'day');
    }
    if (endLocal.isAfter(startLocal)) {
      pairs.push({ start: startLocal, end: endLocal });
    }
    d.add(1, 'day');
  }
  return pairs;
}

function expandWeekly(from, to, startDow, endDow, startHour, startMinute, endHour, endMinute) {
  const pairs = [];
  // Walk back to the start weekday on or before effective-from so the first
  // overlapping window (e.g. Tue–Thu with effective-from on Wed) is included for clamping.
  const cursor = from.clone().startOf('day');
  while (cursor.day() !== startDow) {
    cursor.subtract(1, 'day');
  }

  while (cursor.isSameOrBefore(to, 'day')) {
    const startLocal = cursor.clone().hour(startHour).minute(startMinute).second(0).millisecond(0);
    const daysUntilEnd = (endDow - startDow + 7) % 7;
    let endLocal;
    if (daysUntilEnd === 0) {
      endLocal = startLocal.clone().hour(endHour).minute(endMinute).second(0).millisecond(0);
      if (!endLocal.isAfter(startLocal)) {
        endLocal.add(7, 'days');
      }
    } else {
      endLocal = startLocal.clone().add(daysUntilEnd, 'days').hour(endHour).minute(endMinute).second(0).millisecond(0);
    }

    if (startLocal.isSameOrBefore(to) && endLocal.isSameOrAfter(from)) {
      pairs.push({ start: startLocal, end: endLocal });
    }
    cursor.add(7, 'days');
  }
  return pairs;
}

function expandMonthly(from, to, startDom, endDom, startHour, startMinute, endHour, endMinute) {
  const pairs = [];
  let cursor = from.clone().startOf('month');

  while (cursor.isSameOrBefore(to, 'month')) {
    const dim = cursor.daysInMonth();
    const sDay = Math.min(startDom, dim);
    const startLocal = cursor.clone().date(sDay).hour(startHour).minute(startMinute).second(0).millisecond(0);

    const eDaySame = Math.min(endDom, dim);
    const endSameMonth = cursor.clone().date(eDaySame).hour(endHour).minute(endMinute).second(0).millisecond(0);

    let endLocal;
    if (endSameMonth.isAfter(startLocal)) {
      endLocal = endSameMonth;
    } else {
      const nextMonth = cursor.clone().add(1, 'month').startOf('month');
      const dimNext = nextMonth.daysInMonth();
      const eDay = Math.min(endDom, dimNext);
      endLocal = nextMonth.clone().date(eDay).hour(endHour).minute(endMinute).second(0).millisecond(0);
    }

    if (startLocal.isSameOrBefore(to) && endLocal.isSameOrAfter(from)) {
      pairs.push({ start: startLocal, end: endLocal });
    }
    cursor = cursor.clone().add(1, 'month').startOf('month');
  }
  return pairs;
}

/**
 * @param {Array<{ start: import('moment').Moment, end: import('moment').Moment }>} periods
 * @param {string} timezone
 * @returns {Array<{ start: string, end: string }>}
 */
export function periodsToApiPayload(periods, timezone) {
  return periods.map(({ start, end }) => ({
    start: start.clone().tz(timezone, true).toString(),
    end: end.clone().tz(timezone, true).toString(),
  }));
}
